import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { X, Loader2, MapPinned } from 'lucide-react';
import { RegionCommuneSelector } from './components/RegionCommuneSelector';
import { FileDropzone } from '../../../../components/ui/FileDropzone';
import { FilterableSelect } from '../../../../components/ui/FilterableSelect';
import { createZone, updateZone } from '../../../../features/zones/zonesThunks';
import { fetchRegions } from '../../../../features/geo/geoThunks';
import {
  COUNTRY_OPTIONS,
  ZONE_COUNTRIES,
} from '../../../../features/zones/zonesConstants';
import { extractCommuneIds, isMassiveCountry } from '../../../../features/zones/zonesUtils';
import { selectAdminId, selectIsStaff } from '../../../../features/auth/authSelectors';

const DEFAULT_FORM_STATE = {
  name: '',
  courier: '',
  admin: '',
  price: 0,
  country: '',
  communes: [],
};

const inputClass =
  'w-full bg-ninja-gray/50 dark:bg-[#111] border border-border rounded-xl px-4 py-2.5 text-sm text-ninja-dark dark:text-white outline-none focus:border-ninja-green transition-colors disabled:opacity-60 disabled:cursor-not-allowed';

const labelClass = 'block text-sm font-semibold text-ninja-dark dark:text-white mb-1.5';

const ZoneForm = ({ mode, zone, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const {
    saving,
    courierOptions,
    adminOptions,
    loadingOptions,
  } = useSelector((state) => state.zones);
  const { regionsByCountry, loadingByCountry } = useSelector((state) => state.geo);
  const loadingRegions = Boolean(loadingByCountry[ZONE_COUNTRIES.CL]);

  // Choosing which admin owns the zone belongs to the internal team. An admin
  // owns what it creates, and the backend enforces exactly that: it rejects a
  // zone addressed to somebody else and overwrites the field with the caller.
  const isStaff = useSelector(selectIsStaff);
  const sessionAdminId = useSelector(selectAdminId);

  const isEditMode = mode === 'edit';

  // Filled once, when the form mounts: a blank sheet on create, the stored
  // zone on edit. Never again while open — a late or repeated answer used to
  // wipe the communes ticked so far and the file attached.
  const [formData, setFormData] = useState(() => {
    if (!isEditMode) {
      // The owner travels in the payload either way; for a non-staff admin it
      // is filled in here instead of being picked.
      return {
        ...DEFAULT_FORM_STATE,
        admin: isStaff ? '' : (sessionAdminId ?? ''),
      };
    }

    return {
      name: zone.name || '',
      courier: zone.courier || '',
      admin: zone.admin || '',
      price: zone.price || 0,
      country: zone.country || '',
      communes: extractCommuneIds(zone.communes_set),
    };
  });
  const [file, setFile] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const regions = regionsByCountry[ZONE_COUNTRIES.CL] || [];
  const isMassive = isMassiveCountry(formData.country);

  // The region tree is only needed for manual coverage, so it loads on demand.
  useEffect(() => {
    if (formData.country === ZONE_COUNTRIES.CL) {
      dispatch(fetchRegions(ZONE_COUNTRIES.CL));
    }
  }, [formData.country, dispatch]);

  /**
   * Applies a single field change and clears its pending validation error.
   *
   * @param {string} name
   * @param {string|number} value
   */
  const setField = (name, value) => {
    setFormData((previous) => {
      const next = { ...previous, [name]: value };
      // Coverage does not carry over between selection modes.
      if (name === 'country') next.communes = [];
      return next;
    });

    if (name === 'country') setFile(null);
    if (formErrors[name]) {
      setFormErrors((previous) => ({ ...previous, [name]: null }));
    }
  };

  const handleChange = (event) => {
    setField(event.target.name, event.target.value);
  };

  const handleCommunesChange = (communeIds) => {
    setFormData((previous) => ({ ...previous, communes: communeIds }));
    if (formErrors.communes) {
      setFormErrors((previous) => ({ ...previous, communes: null }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) errors.name = 'El nombre es requerido';

    if ([null, '', undefined].includes(formData.admin)) {
      errors.admin = isStaff
        ? 'Seleccionar Admin es requerido'
        : 'No pudimos identificar tu cuenta de administrador. Vuelve a iniciar sesión.';
    }
    if (!formData.courier) errors.courier = 'El courier es requerido';
    if (!formData.country) errors.country = 'Seleccionar el país es requerido';
    if (formData.price === '' || Number(formData.price) < 0) {
      errors.price = 'Debes ingresar un precio válido';
    }

    if (formData.country === ZONE_COUNTRIES.CL && formData.communes.length === 0) {
      errors.communes = 'Debes seleccionar al menos una comuna';
    }

    // On edit the existing coverage is kept when no new sheet is attached.
    if (formData.country === ZONE_COUNTRIES.MX && !file && !isEditMode) {
      errors.file = 'Debes adjuntar el archivo Excel con los códigos postales';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitError(null);

    if (!validateForm()) return;

    const zoneData = { ...formData, file };

    const action = isEditMode
      ? updateZone({ zoneId: zone.id, zoneData })
      : createZone(zoneData);

    dispatch(action)
      .unwrap()
      .then(() => {
        onSaved?.();
        onClose();
      })
      .catch((error) => {
        setSubmitError(error || 'No se pudo guardar la zona de despacho');
      });
  };

  /** @param {number} communeId */
  const getCommuneName = (communeId) => {
    for (const region of regions) {
      const found = region.communes?.find((commune) => commune.id === communeId);
      if (found) return found.name;
    }
    return communeId;
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
      <div className="p-6 space-y-5 overflow-y-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className={labelClass} htmlFor="zone-name">Nombre de la Zona</label>
            <input
              id="zone-name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ej: Chile - Región Metropolitana"
              className={inputClass}
            />
            {formErrors.name && <p className="text-danger text-xs mt-1">{formErrors.name}</p>}
          </div>

          <div>
            <label className={labelClass} htmlFor="zone-price">Precio</label>
            <input
              id="zone-price"
              name="price"
              type="number"
              min="0"
              value={formData.price}
              onChange={handleChange}
              className={inputClass}
            />
            {formErrors.price && <p className="text-danger text-xs mt-1">{formErrors.price}</p>}
          </div>

          {isStaff && (
            <div>
              <FilterableSelect
                variant="form"
                label="Admin"
                value={formData.admin}
                onChange={(value) => setField('admin', value)}
                options={adminOptions}
                disabled={isEditMode || loadingOptions}
                hasError={Boolean(formErrors.admin)}
                searchPlaceholder="Buscar admin por correo o nombre..."
              />
              {formErrors.admin && <p className="text-danger text-xs mt-1">{formErrors.admin}</p>}
            </div>
          )}

          <div>
            <FilterableSelect
              variant="form"
              label="Courier"
              value={formData.courier}
              onChange={(value) => setField('courier', value)}
              options={courierOptions}
              disabled={loadingOptions}
              hasError={Boolean(formErrors.courier)}
              searchPlaceholder="Buscar courier..."
            />
            {formErrors.courier && <p className="text-danger text-xs mt-1">{formErrors.courier}</p>}
          </div>
        </div>

        <div>
          <FilterableSelect
            variant="form"
            label="País / Método de carga"
            value={formData.country}
            onChange={(value) => setField('country', value)}
            options={COUNTRY_OPTIONS}
            disabled={isEditMode}
            hasError={Boolean(formErrors.country)}
          />
          {formErrors.country && <p className="text-danger text-xs mt-1">{formErrors.country}</p>}
          {isEditMode && (
            <p className="text-xs text-text-muted mt-1.5">
              {isStaff
                ? 'El país y el admin no se pueden modificar una vez creada la zona.'
                : 'El país no se puede modificar una vez creada la zona.'}
            </p>
          )}
        </div>

        {/* Coverage: manual selection */}
        {formData.country === ZONE_COUNTRIES.CL && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={labelClass}>Selección de Comunas</label>
              <span className="text-xs font-semibold text-ninja-green">
                {formData.communes.length} seleccionadas
              </span>
            </div>

            <RegionCommuneSelector
              regions={regions}
              selectedCommuneIds={formData.communes}
              onChange={handleCommunesChange}
              loading={loadingRegions && regions.length === 0}
            />

            {formErrors.communes && (
              <p className="text-danger text-xs mt-1">{formErrors.communes}</p>
            )}

            {formData.communes.length > 0 && (
              <div className="mt-3 p-3 rounded-xl border border-border bg-ninja-gray/40 dark:bg-[#111] max-h-[120px] overflow-y-auto flex flex-wrap gap-1.5">
                {formData.communes.map((communeId) => (
                  <button
                    key={communeId}
                    type="button"
                    onClick={() =>
                      handleCommunesChange(
                        formData.communes.filter((id) => id !== communeId)
                      )
                    }
                    className="group flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-lg bg-ninja-greenLight text-ninja-green text-xs font-semibold border border-ninja-green/20 hover:bg-ninja-green hover:text-white transition-colors"
                    title="Quitar comuna"
                  >
                    {getCommuneName(communeId)}
                    <X size={12} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Coverage: spreadsheet upload */}
        {isMassive && (
          <div>
            <label className={labelClass}>
              {isEditMode
                ? 'Actualizar Códigos Postales (opcional)'
                : 'Carga Masiva de Códigos Postales'}
            </label>
            <FileDropzone
              file={file}
              onFileSelected={(selected) => {
                setFile(selected);
                setFormErrors((previous) => ({ ...previous, file: null }));
              }}
              onClear={() => setFile(null)}
              hint={
                isEditMode
                  ? 'Si no adjuntas un archivo, la cobertura actual se mantiene intacta.'
                  : 'Formatos aceptados: .xlsx y .xls'
              }
            />
            {formErrors.file && <p className="text-danger text-xs mt-2">{formErrors.file}</p>}
          </div>
        )}

        {/* Without the field on screen there is nowhere to hang this one,
            and a form that refuses to save without saying why is worse
            than the missing session it is reporting. */}
        {!isStaff && formErrors.admin && (
          <div className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm">
            {formErrors.admin}
          </div>
        )}

        {submitError && (
          <div className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm">
            {submitError}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="px-5 py-2.5 rounded-xl border border-border text-text-secondary hover:text-ninja-dark dark:hover:text-white hover:border-ninja-dark dark:hover:border-white font-semibold text-sm transition-colors disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-ninja-green hover:bg-ninja-green/90 text-white font-bold text-sm transition-all disabled:opacity-50"
        >
          {saving && <Loader2 size={16} className="animate-spin" />}
          {saving ? 'Guardando...' : isEditMode ? 'Guardar Cambios' : 'Crear Zona'}
        </button>
      </div>
    </form>
  );
};

/**
 * Create/edit form for a shipping zone.
 *
 * The country drives the whole second half of the form: Chilean zones pick
 * their communes from the region tree, Mexican ones receive a spreadsheet of
 * postal codes. Both fan out to different endpoints on save.
 *
 * In edit mode the zone is read from the store rather than from a list row,
 * because the list payload only carries the commune *count* — hydrating the
 * form from it would submit an empty coverage.
 *
 * The form is mounted only while open: each opening starts from the zone as it
 * is then, and nothing that arrives while it is open overwrites the form.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {'create'|'edit'} props.mode
 * @param {number|string|null} [props.zoneId] - On edit, the zone asked for.
 * @param {() => void} props.onClose
 * @param {() => void} props.onSaved - Fired after a successful save
 */
export const ZoneFormModal = ({ isOpen, mode, zoneId = null, onClose, onSaved }) => {
  const { zone, loadingZone, saving } = useSelector((state) => state.zones);

  const isEditMode = mode === 'edit';

  // On edit, the form waits for the zone that was asked for: a slower answer
  // for a zone opened and cancelled before must not fill it. Going back to
  // loading unmounts the form and what was typed, so a refresh of the detail
  // while this is open must not pass through `loadingZone`.
  const zoneReady =
    !isEditMode ||
    (!loadingZone && Boolean(zone) && (zoneId == null || String(zone.id) === String(zoneId)));

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Not closable while saving: the outcome, or the error, lands in here. */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={saving ? undefined : onClose} />

      <div
        role="dialog"
        aria-modal="true"
        aria-busy={saving}
        data-testid="zone-form-modal"
        className="relative bg-white dark:bg-[#1a1a1a] rounded-3xl w-full max-w-2xl shadow-2xl border border-border animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">
              <MapPinned size={20} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-ninja-dark dark:text-white">
                {isEditMode ? 'Editar Zona de Despacho' : 'Crear Nueva Zona'}
              </h3>
              <p className="text-sm text-text-secondary">
                Define el courier, el precio y la cobertura de la zona.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Cerrar"
            className="text-text-muted hover:text-ninja-dark dark:hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={20} />
          </button>
        </div>

        {zoneReady ? (
          <ZoneForm mode={mode} zone={zone} onClose={onClose} onSaved={onSaved} />
        ) : (
          <div className="flex items-center justify-center gap-3 py-20">
            <Loader2 size={20} className="text-ninja-green animate-spin" />
            <span className="text-sm text-text-secondary">Cargando datos de la zona...</span>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
